import { formatDuration, type LevelRecord, type Solve } from '@arrow/game-engine';
import { type Level } from '@arrow/puzzle-schema';
import { useEffect, useId, useRef, useState } from 'react';

import { CHART_MAX, chartScale, chartSummary, shortDate, tickLabel } from './model.ts';

/** The rendered width of an element (ResizeObserver; a sensible width where there's none, e.g. jsdom). */
function useWidth(fallback = 340) {
	const ref = useRef<HTMLDivElement>(null);
	const [width, setWidth] = useState(fallback);
	useEffect(() => {
		const el = ref.current;
		if (!el || typeof ResizeObserver === 'undefined') return;
		const ro = new ResizeObserver(([entry]) => {
			const w = Math.round(entry?.contentRect.width ?? 0);
			if (w > 0) setWidth(w);
		});
		ro.observe(el);
		return () => ro.disconnect();
	}, []);
	return [ref, width] as const;
}

const H = 156; // the whole drawing
const TOP = 18; // room for the Best wedge
const BOTTOM = 22; // the dates
const DIGIT = 7.2; // a tabular figure at --fs-caption: the time labels' gutter grows with the longest
const MIN_SLOTS = 12; // a few solves stay slim, filling in from the left

/**
 * A level's latest solve times, one bar each, oldest on the left, drawn to scale from
 * zero. Your best (no reveal) is the vermilion bar under the brand's wedge; a solve that
 * used a reveal is hatched (its clock stopped early, so it's never a best); the dashed
 * rule is your typical (median) time. The words are its `aria-label` and the key below.
 */
export function TimesChart({ level, solves, record }: { level: Level; solves: Solve[]; record: LevelRecord }) {
	const [ref, width] = useWidth();
	const hatch = `st-hatch-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`;
	const shown = solves.slice(-CHART_MAX);
	const times = shown.map((s) => s.elapsedMs);
	const { topMs, ticksMs } = chartScale(times, record.medianMs);
	// Your typical time over every solve at this level, so recent bars read against it.
	const typical = record.medianMs ?? 0;
	// The labels' gutter fits the longest label ("10:00", "1:00:00") with room for its tick.
	const GUTTER = Math.ceil(Math.max(...ticksMs.map((t) => tickLabel(t).length), 1) * DIGIT) + 12;
	const plotW = Math.max(120, width - GUTTER);
	const slot = plotW / Math.max(shown.length, MIN_SLOTS);
	const barW = Math.min(14, Math.max(3, slot * 0.62));
	const plotH = H - TOP - BOTTOM;
	const y = (ms: number) => TOP + plotH * (1 - ms / topMs);
	const x = (i: number) => GUTTER + slot * i + (slot - barW) / 2;
	const bestIndex = shown.findIndex((s) => s.revealsUsed === 0 && s.elapsedMs === record.bestMs && s.dateKey === record.bestOn);
	const revealed = shown.some((s) => s.revealsUsed > 0);
	const summary = chartSummary(level, shown, record);
	const last = shown.length - 1;

	// Your best wears the brand's wedge, pointing down at it.
	const bestMark = bestIndex >= 0 ? { cx: x(bestIndex) + barW / 2, top: y(shown[bestIndex]!.elapsedMs) } : null;

	return (
		<figure className="st-chart">
			<div ref={ref} className="st-chart-box">
				<svg width={width} height={H} viewBox={`0 0 ${width} ${H}`} role="img" aria-label={summary}>
					<defs>
						<pattern id={hatch} width="4" height="4" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
							<rect width="4" height="4" style={{ fill: 'var(--stock)' }} />
							<line x1="0" y1="0" x2="0" y2="4" style={{ stroke: 'var(--ink)', strokeWidth: 1.6 }} />
						</pattern>
					</defs>
					<g className="st-grid" aria-hidden="true">
						{ticksMs.map((t) => (
							<g key={t}>
								<line x1={GUTTER - 4} x2={width} y1={y(t)} y2={y(t)} />
								<text x={GUTTER - 8} y={y(t) + 4} textAnchor="end">
									{tickLabel(t)}
								</text>
							</g>
						))}
						<line className="st-base" x1={GUTTER - 4} x2={width} y1={y(0)} y2={y(0)} />
						<text x={GUTTER - 8} y={y(0) + 4} textAnchor="end">
							0
						</text>
					</g>
					<g className="st-bars">
						{shown.map((s, i) => {
							// A time past the chart's top runs to the top and is broken there (below).
							const top = y(Math.min(s.elapsedMs, topMs));
							const kind = i === bestIndex ? 'best' : s.revealsUsed > 0 ? 'revealed' : 'plain';
							return (
								<rect
									key={`${s.dateKey}-${i}`}
									className="st-bar"
									data-kind={kind}
									x={x(i)}
									y={top}
									width={barW}
									height={Math.max(1.5, y(0) - top)}
									style={{ ['--i' as string]: i, ...(kind === 'revealed' ? { fill: `url(#${hatch})` } : {}) }}
								/>
							);
						})}
					</g>
					{/* A bar that ran off the top: two paper strokes across it, the printer's break. */}
					<g className="st-breaks" aria-hidden="true">
						{shown.map((s, i) =>
							s.elapsedMs > topMs ? (
								<path
									key={`${s.dateKey}-${i}`}
									d={`M${x(i) - 1} ${TOP + 7} l${barW + 2} -4 M${x(i) - 1} ${TOP + 12} l${barW + 2} -4`}
								/>
							) : null,
						)}
					</g>
					<line className="st-typical" x1={GUTTER - 4} x2={width} y1={y(typical)} y2={y(typical)} />
					{bestMark && (
						<path
							className="st-best-mark"
							d={`M${bestMark.cx - 6} ${bestMark.top - 13} h12 l-6 8 z`}
							aria-hidden="true"
						/>
					)}
					<g className="st-dates" aria-hidden="true">
						<text x={GUTTER} y={H - 4}>
							{shortDate(shown[0]!.dateKey)}
						</text>
						{last > 0 && (
							<text x={Math.min(width, x(last) + barW)} y={H - 4} textAnchor="end">
								{shortDate(shown[last]!.dateKey)}
							</text>
						)}
					</g>
				</svg>
			</div>
			<figcaption className="st-chart-key" aria-hidden="true">
				{bestMark && (
					<span>
						<i className="st-sw" data-kind="best" /> Your best
					</span>
				)}
				<span>
					<i className="st-sw" data-kind="typical" /> Typical {formatDuration(typical)}
				</span>
				{revealed && (
					<span>
						<i className="st-sw" data-kind="revealed" /> Used a reveal
					</span>
				)}
			</figcaption>
		</figure>
	);
}
